import moment from 'moment';
import { generateRealIntervals, generateTimeIntervals, isSameAs, unixDiff } from './times';
import { EPISODE_STATUSES } from '../constants/common';

/**
 * @param {Array} stream
 * @returns {Array}
 */
export const mappedEpisodes = (stream) => {
    return stream.map((stream, index) => {
        return stream.episode.map((episode) => {
            return {
                ...episode,
                streamNumber: index + 1,
                stream,
            };
        });
    })?.flat() ?? [];
};

/**
 * @param {Array} stream
 * @returns {Array}
 */
export const getStreamDates = (stream) => {
    const dates = [];
    let current = moment.unix(stream.timeStart).startOf('day');
    const end = moment.unix(stream.timeStop);

    while (current <= end) {
        dates.push(current.unix());
        current = current.add(1, 'days');
    }

    return dates;
};

/**
 * @param {Array} dates
 * @param {Array} episodes
 * @returns {Array}
 */
export const filterDatesByEpisodes = (dates, episodes) => {
    return dates.filter((date) => episodes.some(({ timeStart }) => isSameAs(timeStart, date)));
};

/**
 * @param {object} event
 * @returns {Array}
 */
export const getStreamsDates = (streams) => {
    const allTimeStartsOfEpisodes = streams.map((stream) => {
        return stream.episode.map(({ timeStart }) => timeStart);
    }).flat();

    return allTimeStartsOfEpisodes.reduce(function (acc, item) {
        const isExist = acc.some((unixItem) => moment.unix(unixItem).format('L') === moment.unix(item).format('L'));

        if (!isExist) acc.push(item);

        return acc;
    }, []).sort((a, b) => a - b);
};

export const episodeMembers = (episodeId, members) => {
    return members.filter(({ episodes }) => episodes.some(({ episode }) => episode.id === episodeId));
};

/**
 * @param {Array} episodes
 * @returns {object}
 */
export const getGridTimelineData = (episodes) => {
    if (!episodes?.length) return null;

    const timeStart = episodes.map(({ timeStart }) => timeStart).sort((a, b) => a - b)[0];
    const timeStop = episodes.map(({ timeStop }) => timeStop).sort((a, b) => a - b).slice(-1)[0];

    const startStopUnix = {
        timeStart: moment.unix(timeStart).startOf('hour').unix(),
        timeStop: moment.unix(timeStop).startOf('hour').unix(),
    };

    return {
        ...startStopUnix,
        diffHours: unixDiff(timeStart, timeStop, 'hours'),
        intervals: generateTimeIntervals(startStopUnix.timeStart, startStopUnix.timeStop, 60, 0),
    };
};

/**
 * @param {object} episode
 * @param {Array} episodes
 * @returns {Array}
 */
export const findEpisodesTimeCrossing = (episode, episodes) => {
    return episodes
        .filter(({ id }) => id !== episode.id)
        .filter((ep) => {
            const result = episode.stream.id !== ep.stream.id
                && ((episode.timeStart < ep.timeStart && episode.timeStop > ep.timeStart)
                    || (episode.timeStart < ep.timeStop && episode.timeStop > ep.timeStop)
                    || (episode.timeStart > ep.timeStart && episode.timeStop < ep.timeStop)
                    || episode.timeStart === ep.timeStart
                    || episode.timeStop === ep.timeStop);

            return result;
        });
};

/**
 * @param {Array} streams - Array of event streams
 * @param {number} date - timestamp
 * @returns {Array} filtered streams by date and if has episodes
 */
export const getHallsByDate = (streams, date) => {
    if (!date) return streams;

    return streams.filter(({ episode }) => {
        return episode.some(({ timeStart, timeStop }) => {
            return isSameAs(date, timeStart) || isSameAs(date, timeStop);
        });
    });
};

/**
 * @param {number} date - Unix
 * @param {Array} episodes
 * @returns {Array}
 */
export const getEpisodesByDate = (date, episodes) => {

    const filtered = episodes.filter(({ timeStart }) => isSameAs(date, timeStart)).sort((a, b) => a.timeStart - b.timeStart);

    const result = [];

    filtered.forEach((episode, index) => {
        let timelineStart = moment.unix(episode.timeStart).startOf('minute');
        let timelineStop = moment.unix(episode.timeStop).startOf('minute');

        if (index && timelineStart.isBefore(moment.unix(result[index - 1].timelineStop))) {
            timelineStart = moment.unix(result[index - 1].timelineStop);
        }

        if (timelineStop.isSameOrBefore(timelineStart)) {
            timelineStop = moment(timelineStart).add(1, 'minutes');
        }

        result.push({
            ...episode,
            timelineStart: timelineStart.unix(),
            timelineStop: timelineStop.unix(),
        })
    })


    return result;
};

/**
 * @param {Array} episodes
 * @returns {object}
 */
export const getBroadcastTimeline = (episodes) => {
    if (!episodes?.length) return null;

    const timeStart = episodes.map(({ timeStart }) => timeStart).sort((a, b) => a - b)[0];
    const timeStop = episodes.map(({ timeStop }) => timeStop).sort((a, b) => b - a)[0];
    const intervals = generateRealIntervals(timeStart, timeStop);

    return {
        timeStart,
        timeStop,
        intervals,
    };
};

/**
 * @param {object} episode
 * @returns {string}
 */
export const getEpisodeStatus = (episode) => {
    if (!episode) {
        console.error(`function getEpisodeStatus: episode type is ${typeof episode}`);

        return null;
    }

    if (moment().unix() < episode.timeStart) return EPISODE_STATUSES.idle;
    if (moment().unix() > episode.timeStop) return EPISODE_STATUSES.record;

    return EPISODE_STATUSES.live;
};

/**
 * @param {object} episode
 * @returns {boolean}
 */
export const isCurrentDayEpisode = (episode) => {
    if (!episode) {
        return false;
    }

    const startOfDay = moment().startOf('day').unix();
    const endOfDay = moment().endOf('day').unix();

    return (
        (episode.timeStart <= endOfDay && episode.timeStop >= startOfDay) ||
        (episode.timeStart <= endOfDay && episode.timeStop > endOfDay) ||
        (episode.timeStart < startOfDay && episode.timeStop >= startOfDay)
    );
};
